import ls from 'localstorage-slim';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchContracts } from '../../../api/instant-lease-api';
import isAuthenticated from '../../../redux/auth/authUtils';
import { setTextKeyVisibility } from '../../../redux/cache-slice';
import { Feature } from '../../../redux/feature-toggles/types';
import { openFeedbackForm } from '../../../redux/feedback/feedback-slice';
import { setContracts } from '../../../redux/leaseApp/lease-app-slice';
import { RootState } from '../../../redux/types';
import { AppStatus, Contracts } from '../../../types/instant-lease-api';
import { getAppStatus } from '../../../utils/getter';
import { errorLogging } from '../../../utils/logging';
import Navigation from '../../../utils/navigation';
import Button from '../../b2b/components/button/button';
import { ReactComponent as GlobeIcon } from '../../b2b/components/icons/globe.svg';
import { ReactComponent as TextIcon } from '../../b2b/components/icons/type.svg';
import PartnerLogo from '../../b2b/components/partner-logo/partner-logo';
import { ReactComponent as CarIcon } from '../icons/car-button.svg';
import { ReactComponent as ChevronDown } from '../icons/chevron-down-icon.svg';
import { ReactComponent as ChevronUp } from '../icons/chevron-up-icon.svg';
import { ReactComponent as ContactIcon } from '../icons/contact-icon.svg';
import { ReactComponent as ExternalLink } from '../icons/external-link-icon.svg';
import { ReactComponent as FeedbackIcon } from '../icons/feedback-icon.svg';
import { ReactComponent as HelpIcon } from '../icons/help-icon.svg';
import { ReactComponent as LogoutIcon } from '../icons/logout-button.svg';
import { ReactComponent as LogoutIconWhite } from '../icons/logout-white.svg';
import { ReactComponent as UserFilledIcon } from '../icons/user-icon-filled.svg';
import { ReactComponent as UserIcon } from '../icons/user-icon.svg';
import SubHeader from '../sub-header/sub-header';
import Tooltip from '../tooltip/tooltip';
import './header.css';

interface HeaderProps {
  showSubHeader?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showSubHeader = true }) => {
  // Cache Store
  const { showTextKey, isSubheaderVisible } = useSelector(
    (state: RootState) => state.cache,
  );

  // Local env
  const isLocalDev = import.meta.env.MODE === 'development';

  // Sequence Store
  const { device } = useSelector((state: RootState) => state.sequence);

  // Auth State
  const { emailVerified, accessToken, expiresAt, email } = useSelector(
    (state: RootState) => state.auth,
  );

  // Lease Application Storage
  const { activeApplication, leaseApplications, contracts, carData } =
    useSelector((state: RootState) => state.leaseApp);

  const { [Feature.Feedback]: feedback } = useSelector(
    (state: RootState) => state.featureToggles,
  );

  // Application status
  const status = getAppStatus(activeApplication);

  // dispatch hook
  const dispatch = useDispatch();

  // Header visibility
  const [visible, setVisible] = useState<boolean>(true);

  // Logout Tooltip visibility
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  // Sub-menu visibility
  const [subMenuVisibility, setSubMenuVisibility] = useState<boolean>(false);

  // Navigation
  const { nextRoute } = new Navigation();

  // Navigate Hook
  const navigate = useNavigate();

  // Current URL
  const url = useLocation().pathname;

  const helpRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const logoutRef = useRef<HTMLDivElement>(null);

  // Get previous contracts
  useEffect(() => {
    if (
      isAuthenticated({
        accessToken,
        expiresAt,
      }) &&
      contracts &&
      contracts?.length <= 0
    ) {
      fetchContracts(accessToken)
        .then(({ contracts }: Contracts) => {
          if (contracts?.length > 0) {
            dispatch(setContracts(contracts));
          }
        })
        .catch((error) => errorLogging(new Error(`${error}`)));
    }
  }, [accessToken, contracts, dispatch, expiresAt]);

  // Control Header visibility
  useEffect(() => {
    const notAccessibleHeaderLocations = [
      'start',
      'login',
      'logout',
      'register',
      'reset-password',
      'invalid-offer-error',
      'contracts-history',
      'change-password',
    ];
    const timeout = setTimeout(() => {
      setVisible(!notAccessibleHeaderLocations.includes(url.split('/')[1]));
    }, 100);
    return () => clearTimeout(timeout);
  }, [emailVerified, url]);

  // Disable scroll in case of desktop
  useEffect(() => {
    if (
      device === 'desktop' &&
      document.body.classList.contains('scroll-disable')
    ) {
      document.body.classList.remove('scroll-disable');
    }
  }, [device]);

  useEffect(() => {
    if (subMenuVisibility) {
      const timeout = setTimeout(() => {
        helpRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [subMenuVisibility]);

  const getUserFullName = (): string => {
    const person =
      leaseApplications[0]?.created_by?.person ||
      activeApplication?.created_by?.person;
    const first = ls.get('first_name') || person?.first_name;
    const last = ls.get('last_name') || person?.last_name;
    return first && last ? `${first} ${last}` : '';
  };

  const toggleSubHeader = (forceState?: boolean) => {
    const isMobile = device === 'mobile';
    const bodyHasClass = document.body.classList.contains('scroll-disable');
    if (isMobile)
      document.body.classList.toggle('scroll-disable', !bodyHasClass);
    setSubMenuVisibility(forceState ?? !subMenuVisibility);
  };

  if (!visible) return null;
  // In the case of failed, credit failed or esignature failed, we want to show the header without the subheader and without the user menu
  if (
    status === AppStatus.Failed ||
    status === AppStatus.CreditFailed ||
    status === AppStatus.EsignatureFailed ||
    status === AppStatus.Timedout ||
    status === undefined
  ) {
    showSubHeader = false;
  }

  return (
    <>
      <div
        className={`header-view-status-container ${
          status === AppStatus.Cancelled ? 'x' : ''
        } ${carData || activeApplication ? 'hasSubheader' : ''} ${
          isSubheaderVisible ? 'active' : ''
        }`}
      >
        <div>
          <div className='header' data-testid='header'>
            {subMenuVisibility && (
              <div className='menu-header'>
                <div className='menu-header-body'>
                  <h3 className='content-bold'>Hallo</h3>
                  <p className='content-normal'>{getUserFullName()}</p>
                  <p className='dark-grey content-normal'>
                    {email}
                    {!emailVerified && (
                      <>
                        {' '}
                        | <span className='light-grey'>Nicht verifiziert</span>
                      </>
                    )}
                  </p>{' '}
                  {contracts && contracts?.length > 0 && (
                    <>
                      <div
                        className='header-section focus-style'
                        role='button'
                        tabIndex={0}
                        aria-label='Meine abgeschlossenen Verträge'
                        onClick={() =>
                          window.open('/contracts-history', '_blank')
                        }
                        onKeyDown={(e) =>
                          ['Enter', ' '].includes(e.key) &&
                          window.open('/contracts-history', '_blank')
                        }
                      >
                        <CarIcon />
                        <span className='dark-grey content-normal'>
                          Meine abgeschlossenen Verträge
                        </span>
                        <ExternalLink
                          className='link-icon'
                          width='20px'
                          height='20px'
                        />
                      </div>
                    </>
                  )}
                  <div
                    ref={helpRef}
                    className='header-section focus-style'
                    role='button'
                    tabIndex={0}
                    aria-label='Hilfe und FAQ öffnen'
                    onClick={() =>
                      window.open('https://www.ald-online.de/faq', '_blank')
                    }
                    onKeyDown={(e) =>
                      ['Enter', ' '].includes(e.key) &&
                      window.open('https://www.ald-online.de/faq', '_blank')
                    }
                  >
                    <HelpIcon />
                    <span className='dark-grey content-normal'>
                      Hilfe | FAQ
                    </span>
                    <ExternalLink
                      className='link-icon'
                      width='20px'
                      height='20px'
                    />
                  </div>
                  <div
                    ref={contactRef}
                    className='header-section focus-style'
                    role='button'
                    tabIndex={0}
                    aria-label='Kontakt öffnen'
                    onClick={() =>
                      window.open(
                        'https://www.ald-online.de/pages/kontaktiere-uns',
                        '_blank',
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        window.open(
                          'https://www.ald-online.de/pages/kontaktiere-uns',
                          '_blank',
                        );
                      } else if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        setSubMenuVisibility(false);
                        setTimeout(() => logoutRef.current?.focus(), 50);
                      }
                    }}
                  >
                    <ContactIcon />
                    <span className='dark-grey content-normal'>Kontakt</span>
                    <ExternalLink
                      className='link-icon'
                      width='20px'
                      height='20px'
                    />
                  </div>
                  {feedback.enabled && (
                    <div
                      className='header-section feedback'
                      style={{ cursor: 'pointer' }}
                      onClick={() => dispatch(openFeedbackForm())}
                    >
                      <FeedbackIcon />
                      <span className='content-bold'>
                        Gib uns dein Feedback
                      </span>
                    </div>
                  )}
                  <div
                    className='header-section logout-section mobile-only'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      window.location.href = '/logout';
                    }}
                  >
                    <LogoutIconWhite />
                    <span className='content-bold'>Jetzt abmelden</span>
                  </div>
                </div>
                <Button
                  type='button'
                  className='submenu-close mobile-only bold'
                  onClick={() => toggleSubHeader(false)}
                >
                  Menü schließen
                </Button>
              </div>
            )}
            <div role='presentation'>
              <PartnerLogo data-testid='header-logo' className='image' />
            </div>

            {accessToken && (
              <div className='header-right-menu'>
                <div
                  className='user-toggle focus-style'
                  role='button'
                  tabIndex={0}
                  aria-label='Benutzermenü öffnen oder schließen'
                  onClick={() => toggleSubHeader()}
                  onKeyDown={(e) => {
                    if (['Enter', ' '].includes(e.key)) {
                      e.preventDefault();
                      toggleSubHeader();
                    }
                  }}
                >
                  {subMenuVisibility ? (
                    <>
                      <UserFilledIcon
                        className='pointer header-icons'
                        width='24px'
                        height='24px'
                        aria-hidden='true'
                      />
                      <span
                        className={`user-name mobile-hidden ${
                          subMenuVisibility
                            ? 'content-bold active'
                            : 'content-normal'
                        }`}
                      >
                        {getUserFullName()}
                      </span>
                      <ChevronUp
                        className='arrow green header-icons'
                        aria-hidden='true'
                      />
                    </>
                  ) : (
                    <>
                      <UserIcon
                        className='pointer header-icons'
                        width='24px'
                        height='24px'
                        aria-hidden='true'
                      />
                      <span className='user-name mobile-hidden content-normal'>
                        {getUserFullName()}
                      </span>
                      <ChevronDown
                        className='arrow header-icons'
                        aria-hidden='true'
                      />
                    </>
                  )}
                </div>

                <span className='mobile-hidden'>|</span>
                <div className='relative' style={{ display: 'flex' }}>
                  <div
                    className='logout-button focus-style'
                    ref={logoutRef}
                    role='button'
                    tabIndex={0}
                    aria-label='Abmelden'
                    onMouseEnter={() => setTooltipVisible(true)}
                    onMouseLeave={() => setTooltipVisible(false)}
                    onClick={() => {
                      window.location.href = '/logout';
                    }}
                    onKeyDown={(e) =>
                      ['Enter', ' '].includes(e.key) &&
                      (window.location.href = '/logout')
                    }
                  >
                    <LogoutIcon
                      className={`button-text mobile-hidden logout header-icons ${
                        tooltipVisible ? 'active' : ''
                      }`}
                      data-testid='logout-Icon'
                      title='Abmeldung'
                      aria-hidden='true'
                    />
                  </div>
                  <Tooltip text='Abmelden' visible={tooltipVisible} />
                </div>
              </div>
            )}
          </div>

          {showSubHeader && <SubHeader />}
          {isLocalDev && (
            <>
              <div className='float-button'>
                {showTextKey ? (
                  <TextIcon
                    width='25px'
                    height='25px'
                    onClick={() => dispatch(setTextKeyVisibility(!showTextKey))}
                  />
                ) : (
                  <GlobeIcon
                    width='25px'
                    height='25px'
                    onClick={() => dispatch(setTextKeyVisibility(!showTextKey))}
                  />
                )}
              </div>
              <Button
                type='button'
                className='pass-button'
                onClick={() => navigate(nextRoute())}
              >
                Pass
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;
